import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Building2, Activity, Users, Stethoscope, Wallet, LogOut, ShieldCheck, ShieldOff, ShieldQuestion, CheckCircle2, XCircle, CreditCard, MessageSquare, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import DoctorVerification from "@/components/DoctorVerification";
import SuperAdminKycQueue from "@/components/SuperAdminKycQueue";

export default function SuperAdminDashboard() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [stats, setStats] = useState(null);
  const [orgs, setOrgs] = useState([]);
  const [txns, setTxns] = useState([]);

  const load = async () => {
    const [s, o, t] = await Promise.all([
      api.get("/superadmin/stats"),
      api.get("/superadmin/orgs"),
      api.get("/superadmin/transactions"),
    ]);
    setStats(s.data);
    setOrgs(o.data);
    setTxns(t.data);
  };

  useEffect(() => { load(); }, []);

  const updateStatus = async (orgId, status) => {
    try {
      await api.post(`/superadmin/orgs/${orgId}/status`, { status });
      toast.success(`Marked ${status}`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
    }
  };

  const doLogout = async () => { await logout(); navigate("/"); };

  if (!stats) return <div className="p-8 text-slate-500">Loading...</div>;

  return (
    <div className="min-h-screen bg-slate-900 text-slate-100" data-testid="superadmin-dashboard-page">
      <header className="bg-slate-950 border-b border-slate-800 sticky top-0 z-10">
        <div className="px-4 sm:px-6 py-3 flex items-center justify-between max-w-[1400px] mx-auto">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-sky-500 to-emerald-500 flex items-center justify-center">
              <ShieldCheck className="w-5 h-5 text-white" />
            </div>
            <div>
              <div className="font-heading text-base font-semibold">Platform Control</div>
              <div className="text-xs text-slate-400">{user?.email}</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => navigate("/superadmin/payouts-management")}
              data-testid="sa-nav-payouts" className="bg-slate-800/50 border-slate-700 text-slate-200 hover:bg-slate-800 gap-1.5">
              <CreditCard className="w-4 h-4" /> Payouts
            </Button>
            <Button variant="ghost" size="icon" onClick={doLogout} className="hover:bg-slate-800" data-testid="sa-logout-btn">
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>

      <main className="px-4 sm:px-6 py-8 max-w-[1400px] mx-auto space-y-8">
        <section className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
          <StatTile icon={Building2} label="Hospitals" value={stats.total_hospitals} accent="sky" />
          <StatTile icon={Activity} label="Clinics" value={stats.total_clinics} accent="emerald" />
          <StatTile icon={Stethoscope} label="Doctors" value={stats.total_doctors} accent="navy" />
          <StatTile icon={Users} label="Patients" value={stats.total_patients} accent="amber" />
          <StatTile icon={Wallet} label="Txn Volume" value={`₹${stats.total_transaction_amount}`} accent="emerald" />
          <StatTile icon={ShieldQuestion} label="Pending" value={stats.pending_orgs} accent="navy" />
        </section>

        <Tabs defaultValue="orgs">
          <TabsList className="bg-slate-800 border-slate-700">
            <TabsTrigger value="orgs" data-testid="sa-tab-orgs">Organizations</TabsTrigger>
            <TabsTrigger value="kyc" data-testid="sa-tab-kyc">KYC Review</TabsTrigger>
            <TabsTrigger value="txns" data-testid="sa-tab-txns">Transactions</TabsTrigger>
            <TabsTrigger value="commission" data-testid="sa-tab-commission">Commission</TabsTrigger>
            <TabsTrigger value="reports" data-testid="sa-tab-reports">Reports</TabsTrigger>
            <TabsTrigger value="verification" data-testid="sa-tab-verification">Verification</TabsTrigger>
            <TabsTrigger value="integrations" data-testid="sa-tab-integrations">Integrations</TabsTrigger>
          </TabsList>

          <TabsContent value="orgs" className="mt-4">
            <Card className="bg-slate-800 border-slate-700">
              <Table>
                <TableHeader>
                  <TableRow className="border-slate-700 hover:bg-slate-800">
                    <TableHead className="text-slate-300">Name</TableHead>
                    <TableHead className="text-slate-300">Type</TableHead>
                    <TableHead className="text-slate-300">City</TableHead>
                    <TableHead className="text-slate-300">Doctors</TableHead>
                    <TableHead className="text-slate-300">Status</TableHead>
                    <TableHead className="text-slate-300 text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {orgs.length === 0 ? (
                    <TableRow className="border-slate-700"><TableCell colSpan={6} className="text-center text-slate-400 py-10">No organizations yet.</TableCell></TableRow>
                  ) : orgs.map((o) => (
                    <TableRow key={o.id} className="border-slate-700 hover:bg-slate-700/40" data-testid={`org-row-${o.id}`}>
                      <TableCell>
                        <div className="font-medium text-white">{o.org_name}</div>
                        <div className="text-xs text-slate-400">{o.email}</div>
                      </TableCell>
                      <TableCell><Badge variant="outline" className="border-slate-600 text-slate-200">{o.org_type}</Badge></TableCell>
                      <TableCell className="text-slate-300">{o.city || "—"}</TableCell>
                      <TableCell className="text-slate-300">{o.doctor_count}</TableCell>
                      <TableCell>
                        <Badge className={
                          o.status === "active" ? "bg-emerald-500/20 text-emerald-300 hover:bg-emerald-500/20" :
                          o.status === "pending" ? "bg-amber-500/20 text-amber-300 hover:bg-amber-500/20" :
                          "bg-rose-500/20 text-rose-300 hover:bg-rose-500/20"
                        }>{o.status}</Badge>
                      </TableCell>
                      <TableCell className="text-right space-x-2">
                        {o.status !== "active" && (
                          <Button size="sm" className="bg-emerald-500 hover:bg-emerald-600 text-white" onClick={() => updateStatus(o.id, "active")} data-testid={`org-approve-${o.id}`}>
                            <ShieldCheck className="w-3.5 h-3.5 mr-1" /> Approve
                          </Button>
                        )}
                        {o.status !== "deactivated" && (
                          <Button size="sm" variant="outline" className="border-rose-500 text-rose-300 hover:bg-rose-500/10" onClick={() => updateStatus(o.id, "deactivated")} data-testid={`org-deactivate-${o.id}`}>
                            <ShieldOff className="w-3.5 h-3.5 mr-1" /> Deactivate
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          </TabsContent>

          <TabsContent value="kyc" className="mt-4">
            <SuperAdminKycQueue />
          </TabsContent>

          <TabsContent value="txns" className="mt-4">
            <Card className="bg-slate-800 border-slate-700">
              <Table>
                <TableHeader>
                  <TableRow className="border-slate-700 hover:bg-slate-800">
                    <TableHead className="text-slate-300">UPI Ref</TableHead>
                    <TableHead className="text-slate-300">Doctor</TableHead>
                    <TableHead className="text-slate-300">Amount</TableHead>
                    <TableHead className="text-slate-300">Status</TableHead>
                    <TableHead className="text-slate-300">Date</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {txns.length === 0 ? (
                    <TableRow className="border-slate-700"><TableCell colSpan={5} className="text-center text-slate-400 py-10">No transactions yet.</TableCell></TableRow>
                  ) : txns.map((t) => (
                    <TableRow key={t.id} className="border-slate-700">
                      <TableCell className="font-mono text-slate-200">{t.upi_ref}</TableCell>
                      <TableCell className="text-slate-300">{t.doctor_id?.slice(0, 8)}…</TableCell>
                      <TableCell className="font-semibold text-emerald-300">₹{t.amount}</TableCell>
                      <TableCell><Badge variant="outline" className="border-slate-600 text-slate-200">{t.status}</Badge></TableCell>
                      <TableCell className="text-slate-400 text-xs">{new Date(t.created_at).toLocaleString()}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          </TabsContent>

          <TabsContent value="commission" className="mt-4">
            <CommissionPanel />
          </TabsContent>

          <TabsContent value="reports" className="mt-4">
            <ReportsPanel />
          </TabsContent>

          <TabsContent value="verification" className="mt-4">
            <DoctorVerification />
          </TabsContent>

          <TabsContent value="integrations" className="mt-4">
            <IntegrationsPanel />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}

function StatTile({ icon: Icon, label, value, accent }) {
  const colors = {
    sky: "from-sky-500/20 to-sky-500/5 text-sky-300 border-sky-500/30",
    emerald: "from-emerald-500/20 to-emerald-500/5 text-emerald-300 border-emerald-500/30",
    navy: "from-slate-500/20 to-slate-500/5 text-slate-200 border-slate-500/30",
    amber: "from-amber-500/20 to-amber-500/5 text-amber-300 border-amber-500/30",
  };
  return (
    <Card className={`p-4 bg-gradient-to-br ${colors[accent]} border`}>
      <div className="flex items-center justify-between">
        <p className="text-[10px] tracking-[0.2em] uppercase font-semibold opacity-90">{label}</p>
        <Icon className="w-4 h-4 opacity-70" />
      </div>
      <p className="font-heading text-2xl sm:text-3xl font-bold mt-2 text-white">{value}</p>
    </Card>
  );
}

function CommissionPanel() {
  const [data, setData] = useState({ rows: [], platform_total_commission: 0 });
  useEffect(() => { api.get("/superadmin/commission").then((r) => setData(r.data)); }, []);
  return (
    <Card className="bg-slate-800 border-slate-700">
      <div className="px-5 py-4 border-b border-slate-700 flex items-center justify-between">
        <h3 className="font-heading text-lg font-semibold text-white">Platform Commission by Org</h3>
        <div className="text-sm text-emerald-300">Total: <span className="font-bold">₹{data.platform_total_commission}</span></div>
      </div>
      <Table>
        <TableHeader>
          <TableRow className="border-slate-700 hover:bg-slate-800">
            <TableHead className="text-slate-300">Org</TableHead>
            <TableHead className="text-slate-300">Type</TableHead>
            <TableHead className="text-slate-300">City</TableHead>
            <TableHead className="text-slate-300">Completed</TableHead>
            <TableHead className="text-slate-300">Per patient</TableHead>
            <TableHead className="text-slate-300 text-right">Commission</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {data.rows.length === 0 ? (
            <TableRow className="border-slate-700"><TableCell colSpan={6} className="text-center text-slate-400 py-10">No commission earned yet. Hospital admins set their per-patient fee in Org Settings.</TableCell></TableRow>
          ) : data.rows.map((r) => (
            <TableRow key={r.org_id} className="border-slate-700" data-testid={`commission-row-${r.org_id}`}>
              <TableCell className="text-white">{r.org_name}</TableCell>
              <TableCell className="text-slate-300">{r.org_type}</TableCell>
              <TableCell className="text-slate-300">{r.city || "—"}</TableCell>
              <TableCell className="text-slate-300">{r.completed_patients}</TableCell>
              <TableCell className="text-slate-300">₹{r.commission_per_patient}</TableCell>
              <TableCell className="text-right font-semibold text-emerald-300">₹{r.total_commission}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Card>
  );
}

function ReportsPanel() {
  const [rows, setRows] = useState([]);
  const [filters, setFilters] = useState({ date: "", q: "" });
  const search = async () => {
    const p = new URLSearchParams();
    if (filters.date) p.set("date", filters.date);
    if (filters.q) p.set("q", filters.q);
    const { data } = await api.get(`/superadmin/reports?${p}`);
    setRows(data);
  };
  return (
    <Card className="bg-slate-800 border-slate-700 p-5">
      <h3 className="font-heading text-lg font-semibold text-white mb-3">Patient Reports</h3>
      <div className="grid sm:grid-cols-3 gap-3">
        <input type="date" value={filters.date} onChange={(e) => setFilters({ ...filters, date: e.target.value })} className="h-10 px-3 rounded-md bg-slate-900 border border-slate-700 text-sm text-white" data-testid="sa-report-date" />
        <input type="text" placeholder="Search by name or mobile" value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })} className="h-10 px-3 rounded-md bg-slate-900 border border-slate-700 text-sm sm:col-span-2 text-white" data-testid="sa-report-q" />
      </div>
      <Button onClick={search} className="mt-3 bg-sky-500 hover:bg-sky-600 text-white" data-testid="sa-report-search-btn">Search</Button>
      <div className="mt-5 overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="border-slate-700">
              <TableHead className="text-slate-300">Date</TableHead>
              <TableHead className="text-slate-300">Patient</TableHead>
              <TableHead className="text-slate-300">Mobile</TableHead>
              <TableHead className="text-slate-300">Doctor</TableHead>
              <TableHead className="text-slate-300">Status</TableHead>
              <TableHead className="text-slate-300 text-right">Fee</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow className="border-slate-700"><TableCell colSpan={6} className="text-center text-slate-400 py-8">Apply a filter and search.</TableCell></TableRow>
            ) : rows.map((r) => (
              <TableRow key={r.id} className="border-slate-700" data-testid={`sa-report-row-${r.id}`}>
                <TableCell className="text-slate-300 text-xs">{r.date}</TableCell>
                <TableCell className="text-white">{r.name} <span className="text-xs text-slate-400">· {r.age}</span></TableCell>
                <TableCell className="text-slate-300">{r.mobile}</TableCell>
                <TableCell className="text-slate-300">{r.doctor_name}</TableCell>
                <TableCell><Badge variant="outline" className="border-slate-600 text-slate-200">{r.status}</Badge></TableCell>
                <TableCell className="text-right text-emerald-300">₹{r.fee}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </Card>
  );
}

function IntegrationsPanel() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    api.get("/superadmin/integrations/status")
      .then((r) => setData(r.data))
      .catch((e) => setErr(e.response?.data?.detail || e.message));
  }, []);
  if (err) return <Card className="bg-slate-800 border-slate-700 p-5 text-rose-300" data-testid="sa-integrations-error">{err}</Card>;
  if (!data) return <Card className="bg-slate-800 border-slate-700 p-5 text-slate-400">Loading…</Card>;
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="sa-integrations-panel">
      <IntegrationCard
        icon={CreditCard}
        title="Razorpay"
        configured={data.razorpay?.configured}
        rows={[
          { k: "Key ID", v: data.razorpay?.key_id_preview || "—" },
          { k: "Webhook Secret", v: data.razorpay?.webhook_secret_configured ? "set" : "missing" },
        ]}
        testid="integ-card-razorpay"
      />
      <IntegrationCard
        icon={MessageSquare}
        title="SMS (MSG91 / Fast2SMS)"
        configured={data.sms?.configured}
        rows={[
          { k: "Primary", v: data.sms?.primary || "none" },
          { k: "Sender", v: data.sms?.msg91_sender || "—" },
        ]}
        testid="integ-card-sms"
      />
      <IntegrationCard
        icon={MessageCircle}
        title="WhatsApp Cloud API"
        configured={data.whatsapp?.configured}
        rows={[
          { k: "Phone Number", v: data.whatsapp?.phone_number_id || "—" },
          { k: "Verify Token", v: data.whatsapp?.verify_token_configured ? "set" : "missing" },
        ]}
        testid="integ-card-whatsapp"
      />
    </div>
  );
}

function IntegrationCard({ icon: Icon, title, configured, rows, testid }) {
  return (
    <Card className="bg-slate-800 border-slate-700 p-4" data-testid={testid}>
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-lg bg-slate-900 flex items-center justify-center text-sky-300">
            <Icon className="w-4 h-4" />
          </div>
          <div>
            <div className="font-semibold text-white">{title}</div>
            <div className="text-xs text-slate-400">3rd-party gateway</div>
          </div>
        </div>
        {configured ? (
          <Badge className="bg-emerald-500/20 text-emerald-300 hover:bg-emerald-500/20 gap-1">
            <CheckCircle2 className="w-3 h-3" /> Live
          </Badge>
        ) : (
          <Badge className="bg-amber-500/20 text-amber-300 hover:bg-amber-500/20 gap-1">
            <XCircle className="w-3 h-3" /> Not configured
          </Badge>
        )}
      </div>
      <div className="mt-3 space-y-1 text-xs">
        {rows.map((r, i) => (
          <div key={i} className="flex justify-between border-b border-slate-700 last:border-b-0 py-1">
            <span className="text-slate-400">{r.k}</span>
            <span className="text-slate-200 font-mono truncate max-w-[60%]" title={r.v}>{r.v}</span>
          </div>
        ))}
      </div>
      {!configured && (
        <p className="text-[11px] text-slate-500 mt-2 leading-relaxed">
          Set the env vars in <span className="font-mono">backend/.env</span> and restart the backend to enable this integration.
        </p>
      )}
    </Card>
  );
}
